import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Plus, Download, ShoppingCart, DollarSign, BarChart3 } from "lucide-react";
import { useReports } from "@/hooks/useReports";
import { TabButton } from "@/components/reports/TabButton";
import { PeriodSelector } from "@/components/reports/PeriodSelector";
import { CustomPeriodDialog } from "@/components/reports/CustomPeriodDialog";
import { OrdersReport } from "@/components/reports/OrdersReport";
import { ProcurementReport } from "@/components/reports/ProcurementReport";
import { AccountingReport } from "@/components/reports/AccountingReport";
import { ExportsSection } from "@/components/reports/ExportsSection";

export default function ReportsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("orders");
  const [showNewReport, setShowNewReport] = useState(false);

  const {
    reportData,
    procurementData,
    accountingData,
    loading,
    selectedPeriod,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    handlePeriodChange,
    handleCustomReport,
    downloadCsv,
    getCsvParams,
  } = useReports();

  const procSummary = procurementData?.summary || { items_to_buy: 0 };
  const showPeriodSelector = activeTab !== "procurement" && activeTab !== "exports";

  if (loading && !reportData) return <MainLayout><LoadingScreen /></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="reports-title">{t("reports.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("reports.subtitle")}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setShowNewReport(true)} data-testid="custom-period-btn">
              <Plus className="w-4 h-4" /> {t("reports.customPeriod")}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg w-fit" data-testid="report-tabs">
          <TabButton active={activeTab === "orders"} onClick={() => setActiveTab("orders")} icon={BarChart3} testId="tab-orders">
            {t("reports.tabs.sales")}
          </TabButton>
          <TabButton active={activeTab === "procurement"} onClick={() => setActiveTab("procurement")} icon={ShoppingCart} badge={procSummary.items_to_buy} testId="tab-procurement">
            {t("reports.tabs.procurement")}
          </TabButton>
          <TabButton active={activeTab === "accounting"} onClick={() => setActiveTab("accounting")} icon={DollarSign} testId="tab-accounting">
            {t("reports.tabs.accounting")}
          </TabButton>
          <TabButton active={activeTab === "exports"} onClick={() => setActiveTab("exports")} icon={Download} testId="tab-exports">
            {t("reports.tabs.exports")}
          </TabButton>
        </div>

        {/* Period Selector */}
        {showPeriodSelector && (
          <PeriodSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} />
        )}

        {/* Tab Content */}
        {activeTab === "orders" && <OrdersReport reportData={reportData} />}
        {activeTab === "procurement" && <ProcurementReport procurementData={procurementData} />}
        {activeTab === "accounting" && (
          <AccountingReport
            accountingData={accountingData}
            onDownloadCsv={downloadCsv}
            getCsvParams={getCsvParams}
          />
        )}
        {activeTab === "exports" && (
          <ExportsSection getCsvParams={getCsvParams} />
        )}

        {/* Custom Period Dialog */}
        <CustomPeriodDialog
          open={showNewReport}
          onOpenChange={setShowNewReport}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onGenerate={() => handleCustomReport(() => setShowNewReport(false))}
        />
      </div>
    </MainLayout>
  );
}
